import emailjs from '@emailjs/browser';
import { QuotationRequest, Invoice } from '../types';
import { LedgerSummary, Company } from '../types/billing';

// EmailJS configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your-service-id';
const EMAILJS_TEMPLATE_QUOTE = process.env.REACT_APP_EMAILJS_TEMPLATE_QUOTE || 'your-quote-template';
const EMAILJS_TEMPLATE_INVOICE = process.env.REACT_APP_EMAILJS_TEMPLATE_INVOICE || 'your-invoice-template';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your-public-key';

export interface EmailTemplateData {
  to_email: string;
  to_name: string;
  subject: string;
  message: string;
  [key: string]: any;
}

// Initialize EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

export const emailService = {
  // Send quote confirmation to client
  async sendQuoteConfirmation(quote: QuotationRequest): Promise<boolean> {
    try {
      const templateData: EmailTemplateData = {
        to_email: quote.email,
        to_name: quote.clientName,
        subject: 'Quote Request Received - AVIS Engineering',
        message: `Dear ${quote.clientName},

Thank you for your interest in AVIS Engineering's NDT services. We have received your quote request and our team is reviewing it.

Quote Details:
- Service Type: ${quote.serviceType}
- Description: ${quote.description}
- Submitted: ${new Date(quote.timestamp).toLocaleDateString()}

Our team will get back to you within 24-48 hours with a detailed quotation.

Best regards,
AVIS Engineering Team
Phone: +1 (555) 123-4567
Email: info@avisengineering.com`,
        service_type: quote.serviceType,
        description: quote.description,
        submission_date: new Date(quote.timestamp).toLocaleDateString()
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_QUOTE,
        templateData
      );

      console.log('Quote confirmation email sent:', result);
      return true;
    } catch (error) {
      console.error('Failed to send quote confirmation:', error);
      return false;
    }
  },

  // Send quote approval notification to client
  async sendQuoteApproval(quote: QuotationRequest, amount: number): Promise<boolean> {
    try {
      const templateData: EmailTemplateData = {
        to_email: quote.email,
        to_name: quote.clientName,
        subject: 'Quote Approved - AVIS Engineering',
        message: `Dear ${quote.clientName},

Great news! Your quote request has been approved.

Quote Details:
- Service Type: ${quote.serviceType}
- Description: ${quote.description}
- Approved Amount: ₹${amount.toLocaleString()}

Our team will prepare the necessary documentation and contact you shortly to proceed with the project.

Best regards,
AVIS Engineering Team
Phone: +1 (555) 123-4567
Email: info@avisengineering.com`,
        service_type: quote.serviceType,
        description: quote.description,
        approved_amount: `₹${amount.toLocaleString()}`
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_QUOTE,
        templateData
      );

      console.log('Quote approval email sent:', result);
      return true;
    } catch (error) {
      console.error('Failed to send quote approval:', error);
      return false;
    }
  },

  // Send invoice notification to client
  async sendInvoiceNotification(invoice: Invoice): Promise<boolean> {
    try {
      const templateData: EmailTemplateData = {
        to_email: invoice.email,
        to_name: invoice.clientName,
        subject: `Invoice Generated - ${invoice.invoiceNumber}`,
        message: `Dear ${invoice.clientName},

Your invoice has been generated and is ready for your review.

Invoice Details:
- Invoice Number: ${invoice.invoiceNumber}
- Service Type: ${invoice.serviceType}
- Amount: ₹${invoice.amount.toLocaleString()}
- Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}

You can view and download your invoice through your client portal at:
https://avisengineering.com/client/login

Best regards,
AVIS Engineering Team
Phone: +1 (555) 123-4567
Email: info@avisengineering.com`,
        invoice_number: invoice.invoiceNumber,
        service_type: invoice.serviceType,
        amount: `₹${invoice.amount.toLocaleString()}`,
        due_date: new Date(invoice.dueDate).toLocaleDateString()
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_INVOICE,
        templateData
      );

      console.log('Invoice notification email sent:', result);
      return true;
    } catch (error) {
      console.error('Failed to send invoice notification:', error);
      return false;
    }
  },

  // Send payment reminder
  async sendPaymentReminder(invoice: Invoice, daysOverdue: number): Promise<boolean> {
    try {
      const urgency = daysOverdue > 30 ? 'URGENT' : 'Reminder';

      const templateData: EmailTemplateData = {
        to_email: invoice.email,
        to_name: invoice.clientName,
        subject: `${urgency}: Payment Due for Invoice ${invoice.invoiceNumber}`,
        message: `Dear ${invoice.clientName},

This is a ${urgency.toLowerCase()} regarding your outstanding payment.

Invoice Details:
- Invoice Number: ${invoice.invoiceNumber}
- Amount Due: ₹${invoice.amount.toLocaleString()}
- Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}
- Days Overdue: ${daysOverdue}

Please arrange payment at your earliest convenience to avoid any service interruptions.

Payment can be made through:
- Online Portal: https://avisengineering.com/client/login
- Bank Transfer: Account details available in your invoice

Best regards,
AVIS Engineering Team
Phone: +1 (555) 123-4567
Email: info@avisengineering.com`,
        invoice_number: invoice.invoiceNumber,
        amount_due: `₹${invoice.amount.toLocaleString()}`,
        due_date: new Date(invoice.dueDate).toLocaleDateString(),
        days_overdue: daysOverdue.toString(),
        urgency
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_INVOICE,
        templateData
      );

      console.log('Payment reminder email sent:', result);
      return true;
    } catch (error) {
      console.error('Failed to send payment reminder:', error);
      return false;
    }
  },

  // Send internal notification to admin
  async sendAdminNotification(type: 'quote' | 'invoice' | 'payment', data: any): Promise<boolean> {
    try {
      const templateData: EmailTemplateData = {
        to_email: 'admin@avisengineering.com', // Admin email
        to_name: 'AVIS Admin',
        subject: `New ${type.charAt(0).toUpperCase() + type.slice(1)} - AVIS Engineering`,
        message: generateAdminMessage(type, data),
        type,
        ...data
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_QUOTE, // Using quote template for admin notifications
        templateData
      );

      console.log('Admin notification email sent:', result);
      return true;
    } catch (error) {
      console.error('Failed to send admin notification:', error);
      return false;
    }
  },

  // Send ledger balance to company
  async sendLedgerBalance(summary: LedgerSummary, company: Company): Promise<boolean> {
    try {
      const formatCurrency = (value?: number) => {
        if (value === undefined || value === null) return 'Rs. 0';
        const rounded = Math.round(value);
        return 'Rs. ' + new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(rounded);
      };

      const totalBilled = summary.accounts.find(a => a.ledgerName.includes('Sales'))?.creditBalance || 0;
      const totalReceived = summary.accounts.find(a => a.ledgerName.includes('Cash/Bank'))?.debitBalance || 0;
      const outstanding = summary.accounts.find(a => a.ledgerName.includes('Outstanding'))?.debitBalance || 0;

      const ledgerDetails = summary.accounts
        .map(acc => `  - ${acc.ledgerName}: Debit ${formatCurrency(acc.debitBalance)}, Credit ${formatCurrency(acc.creditBalance)}`)
        .join('\n');

      const templateData: EmailTemplateData = {
        to_email: company.email,
        to_name: company.name,
        subject: `Ledger Balance Statement - ${summary.periodLabel}`,
        message: `Dear ${company.name},

Please find below your ledger balance statement for the period: ${summary.periodLabel}

Financial Summary:
- Total Billed: ${formatCurrency(totalBilled)}
- Total Received: ${formatCurrency(totalReceived)}
- Outstanding Amount: ${formatCurrency(outstanding)}

Ledger Details:
${ledgerDetails}

Please review the above statement. If you have any questions or concerns, please contact us.

Best regards,
AVIS Engineering Team
Phone: +1 (555) 123-4567
Email: info@avisengineering.com`,
        company_name: company.name,
        period_label: summary.periodLabel,
        total_billed: formatCurrency(totalBilled),
        total_received: formatCurrency(totalReceived),
        outstanding: formatCurrency(outstanding),
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_INVOICE, // Using invoice template for ledger emails
        templateData
      );

      console.log('Ledger balance email sent:', result);
      return true;
    } catch (error) {
      console.error('Failed to send ledger balance:', error);
      return false;
    }
  }
};

function generateAdminMessage(type: string, data: any): string {
  switch (type) {
    case 'quote':
      return `New quote request received:

Client: ${data.clientName}
Email: ${data.email}
Service: ${data.serviceType}
Description: ${data.description}

Please review and respond within 24-48 hours.`;

    case 'invoice':
      return `Invoice generated:

Client: ${data.clientName}
Invoice: ${data.invoiceNumber}
Amount: ₹${data.amount.toLocaleString()}
Due Date: ${new Date(data.dueDate).toLocaleDateString()}`;

    case 'payment':
      return `Payment received:

Client: ${data.clientName}
Amount: ₹${data.amount.toLocaleString()}
Mode: ${data.mode}
Reference: ${data.reference || 'N/A'}`;

    default:
      return 'New activity in the system. Please check the admin panel.';
  }
}

// Utility function to send quote confirmation and admin notification
export const sendQuoteNotifications = async (quote: QuotationRequest): Promise<void> => {
  const promises = [
    emailService.sendQuoteConfirmation(quote),
    emailService.sendAdminNotification('quote', quote)
  ];

  await Promise.allSettled(promises);
};

// Utility function to send invoice notification and admin notification
export const sendInvoiceNotifications = async (invoice: Invoice): Promise<void> => {
  const promises = [
    emailService.sendInvoiceNotification(invoice),
    emailService.sendAdminNotification('invoice', invoice)
  ];

  await Promise.allSettled(promises);
};

export default emailService;
